<template>
    <div class="widget-config-select-extended">
        <div :class="horizontal ? 'flex gap-3' : 'space-y-3'">
            <div :class="{ 'flex-1 min-w-0': horizontal }">
                <RedesignFormLabel
                    :label="$t('widget.selectCategory')"
                    class="mb-1"
                />
                <RedesignBaseSelect
                    :value="selectedCategoryId"
                    :multiple="false"
                    :loading="loading"
                    filterable
                    full-width
                    :class="{ 'border-destructive-actions rounded': errorMessage }"
                    @change="onCategoryChange"
                >
                    <el-option
                        v-for="category in categories"
                        :key="category.categoryID"
                        :label="category.categoryName"
                        :value="category.categoryID"
                    />
                </RedesignBaseSelect>
            </div>

            <div :class="{ 'flex-1 min-w-0': horizontal }">
                <RedesignFormLabel
                    :label="$t('widget.selectQuestion')"
                    class="mb-1"
                />
                <RedesignBaseSelect
                    :value="selectedQuestionLabel"
                    :multiple="false"
                    :loading="loading"
                    filterable
                    full-width
                    :class="{ 'border-destructive-actions rounded': errorMessage }"
                    @change="onQuestionChange"
                >
                    <el-option
                        v-for="question in currentQuestions"
                        :key="question.label"
                        :label="question.label"
                        :value="question.label"
                    >
                        <RedesignTooltip
                            :placement="isRTL ? 'right' : 'left'"
                            :disabled="!question.description"
                        >
                            <template #tooltip-content>
                                <div class="custom-tooltip-content">
                                    <div>
                                        <span class="font-semibold">{{ $t('general.question') }}:</span>
                                        {{ question.description }}
                                    </div>
                                    <div v-if="question.data_type" class="mt-2">
                                        <span class="font-semibold">{{ $t('general.dataType') }}:</span>
                                        {{ question.data_type }}
                                    </div>
                                </div>
                            </template>
                            <span class="block truncate">{{ question.label }}</span>
                        </RedesignTooltip>
                    </el-option>
                </RedesignBaseSelect>
            </div>
        </div>

        <RedesignFormError
            v-if="errorMessage"
            :error-message="errorMessage"
            class="mt-1"
        />
    </div>
</template>

<script setup lang="ts">
import { Option as ElOption } from 'element-ui'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import RedesignFormError from '@/components/RedesignBaseComponents/Form/RedesignFormError.vue'
import RedesignTooltip from '@/components/RedesignBaseComponents/RedesignTooltip.vue'
import { WidgetConfig } from '@/types/dashboard'
import { computed, onMounted, ref } from 'vue'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import {
    getTemplateParameterHelpDescription,
    getTemplateParameterHelpName
} from '@/components/RedesignComponents/Widgets/widget.helper'
import { widgetParameterApi, type QuestionCategoriesMap, type QuestionCategory } from '@/api/widgetParameterApi'
import { isRTL } from '@/composables/useDirection'

const props = withDefaults(defineProps<{
    value: WidgetConfig
    required?: boolean
    errorMessage?: string
    horizontal?: boolean
}>(), {
    required: false,
    errorMessage: '',
    horizontal: false
})

const emit = defineEmits<{
    (e: 'input', payload: WidgetConfig): void
    (e: 'update:value', payload: WidgetConfig): void
}>()

const categoriesMap = ref<QuestionCategoriesMap>({})
const selectedCategoryId = ref<number | ''>('')
const loading = ref(false)

const model = computed({
    get () {
        return props.value
    },
    set (val) {
        emit('input', val)
        emit('update:value', val)
    }
})

const templateParameterHelpName = computed(() => getTemplateParameterHelpName(props.value))
const templateParameterHelpDescription = computed(() => getTemplateParameterHelpDescription(props.value))
const selectedQuestionLabel = computed(() => model.value.WidgetParameterValue ?? '')

const categories = computed<QuestionCategory[]>(() => Object.values(categoriesMap.value))

const currentQuestions = computed(() => {
    if (!selectedCategoryId.value) return []
    return categoriesMap.value[String(selectedCategoryId.value)]?.questions || []
})

function findCategoryByQuestion (questionLabel: string): QuestionCategory | undefined {
    return categories.value.find(cat =>
        cat.questions.some(q => q.label === questionLabel)
    )
}

function initializeSelection () {
    const allCategories = categories.value
    if (!allCategories.length) return

    const currentValue = model.value.WidgetParameterValue

    if (currentValue) {
        const matchingCategory = findCategoryByQuestion(String(currentValue))
        if (matchingCategory) {
            selectedCategoryId.value = matchingCategory.categoryID
            return
        }
    }

    const firstCategory = allCategories[0]
    selectedCategoryId.value = firstCategory.categoryID

    if (firstCategory.questions.length) {
        updateValue(firstCategory.questions[0].label)
    }
}

function updateValue (questionLabel: string) {
    model.value.WidgetParameterValue = questionLabel
    emit('update:value', model.value)
}

function onCategoryChange (categoryId: number) {
    selectedCategoryId.value = categoryId
    const category = categoriesMap.value[String(categoryId)]
    if (category?.questions.length) {
        updateValue(category.questions[0].label)
    }
}

function onQuestionChange (questionLabel: string) {
    if (questionLabel === selectedQuestionLabel.value) return
    updateValue(questionLabel)
}

async function getData () {
    loading.value = true
    try {
        categoriesMap.value = await widgetParameterApi.getQuestionsList()
        initializeSelection()
    } finally {
        loading.value = false
    }
}

onMounted(getData)
</script>
